import axios from "axios";
import Vue from "vue";

// Helper to access loading plugin
const getLoadingPlugin = () => {
  // Try to access from Vue prototype (plugin is installed on Vue.prototype.$loading)
  if (Vue && Vue.prototype && Vue.prototype.$loading) {
    return Vue.prototype.$loading;
  }
  return null;
};

// Helper to access toast plugin
const getToastPlugin = () => {
  // Try to access from Vue prototype (plugin is installed on Vue.prototype.$toast)
  if (Vue && Vue.prototype && Vue.prototype.$toast) {
    return Vue.prototype.$toast;
  }
  return null;
};

const base64ToPdfBlob = (base64) => {
  const binaryData = atob(base64);
  const bytes = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    bytes[i] = binaryData.charCodeAt(i);
  }

  return new Blob([bytes], { type: "application/pdf" });
};

const readBlobAsText = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const getFilenameFromDisposition = (disposition) => {
  if (!disposition) {
    return "";
  }

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match && match[1] ? match[1] : "";
};

const extractPdfBlobFromResponse = async (response) => {
  const contentType =
    (response.headers && response.headers["content-type"]) || "";
  const isJsonResponse =
    contentType.includes("application/json") ||
    ((response.data && response.data.type) || "").includes("application/json");

  if (response.data && isJsonResponse) {
    const payload = JSON.parse(await readBlobAsText(response.data));
    if (!payload.output) {
      throw new Error(payload.message || "PDF output is missing.");
    }

    return base64ToPdfBlob(payload.output);
  }

  if (response.data && response.data.output) {
    return base64ToPdfBlob(response.data.output);
  }

  if (response.data instanceof Blob) {
    return response.data;
  }

  return new Blob([response.data], { type: "application/pdf" });
};

export const printData = async (
  data,
  api,
  method = "POST",
  params = null,
  vm = null
) => {
  // Get loading and toast plugins from Vue prototype
  const loading = getLoadingPlugin();
  const toast = getToastPlugin();

  try {
    // Démarre le loading s'il est disponible
    if (loading && loading.start) {
      loading.start();
    }

    // Construire l'URL API (encodeURIComponent: références facture avec « / », etc.)
    let apiUrl = api;
    const methodName = (method || "POST").toUpperCase();
    if (methodName === "GET" && params != null && params !== "") {
      apiUrl += `/${encodeURIComponent(String(params))}`;
    }

    const config = {
      responseType: "blob",
      timeout: 30000,
    };

    let response;
    if (methodName === "GET") {
      response = await axios.get(apiUrl, config);
    } else {
      response = await axios[methodName.toLowerCase()](apiUrl, data, config);
    }

    // Convertir la réponse base64 en PDF
    const contentType =
      (response.headers && response.headers["content-type"]) || "";
    const contentDisposition =
      (response.headers && response.headers["content-disposition"]) || "";

    let blob = response.data;
    let filename =
      getFilenameFromDisposition(contentDisposition) || "document.pdf";

    const isJsonResponse =
      contentType.includes("application/json") ||
      ((response.data && response.data.type) || "").includes(
        "application/json"
      );

    if (response.data && isJsonResponse) {
      const payload = JSON.parse(await readBlobAsText(response.data));
      if (!payload.output) {
        throw new Error("PDF output is missing from print response.");
      }

      blob = base64ToPdfBlob(payload.output);
      filename = payload.filename || filename;
    } else if (response.data && response.data.output) {
      blob = base64ToPdfBlob(response.data.output);
      filename = response.data.filename || filename;
    } else if (!(response.data instanceof Blob)) {
      blob = new Blob([response.data], { type: "application/pdf" });
    }
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up URL after download
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    // Message de succès
    if (toast && toast.success) {
      toast.success("Téléchargement terminé avec succès !");
    }
  } catch (error) {
    console.error("Error printing PDF:", error);
    if (toast && toast.error) {
      toast.error("Une erreur est survenue lors de l'impression.");
    }
  } finally {
    // Arrête le loading quoi qu'il arrive
    if (loading && loading.stop) {
      loading.stop();
    }
  }
};

export const printDataToPeascomPrinter = async (
  data,
  api,
  method = "POST",
  params = null,
  options = {}
) => {
  const loading = getLoadingPlugin();
  const toast = getToastPlugin();

  try {
    if (loading && loading.start) {
      loading.start();
    }

    let apiUrl = api;
    const methodName = (method || "POST").toUpperCase();
    if (methodName === "GET" && params != null && params !== "") {
      apiUrl += `/${encodeURIComponent(String(params))}`;
    }

    const config = {
      responseType: "blob",
      timeout: 30000,
    };

    let response;
    if (methodName === "GET") {
      response = await axios.get(apiUrl, config);
    } else {
      response = await axios[methodName.toLowerCase()](apiUrl, data, config);
    }

    const pdfBlob = await extractPdfBlobFromResponse(response);
    const base64Data = await blobToBase64(pdfBlob);
    const printerUrl = options.printerUrl || "http://localhost:5010/";
    const payload = { base64Data };

    if (options.printerName) {
      payload.printerName = options.printerName;
    }

    const printerResponse = await fetch(printerUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const printerMessage = await printerResponse.text();
    if (!printerResponse.ok) {
      throw new Error(
        printerMessage || `Peascom Printer error ${printerResponse.status}`
      );
    }

    if (toast && toast.success) {
      toast.success("Impression envoyee a Peascom Printer.");
    }

    return printerMessage;
  } catch (error) {
    console.error("Error sending PDF to Peascom Printer:", error);

    if (toast && toast.error) {
      const message =
        error && error.message && error.message.includes("Failed to fetch")
          ? "Peascom Printer n'est pas demarre sur http://localhost:5010."
          : "Une erreur est survenue lors de l'impression.";
      toast.error(message);
    }
  } finally {
    if (loading && loading.stop) {
      loading.stop();
    }
  }
};

/*import axios from "axios";

export const printData = async (
  data,
  api,
  method,
  params = null,
  vm = null
) => {
  try {
    // Démarre le loading s'il est disponible
    if (vm && vm.$loading) vm.$loading.start();

    // Construire l'URL API
    let apiUrl = api;
    if (method.toUpperCase() === "GET" && params) {
      apiUrl += `/${params}`;
    }

    const config = {
      responseType: "json",
    };

    if (method.toUpperCase() === "GET") {
      config.params = {};
    } else {
      config.data = data;
    }

    const response = await axios[method.toLowerCase()](apiUrl, config);

    // 👉 Envoyer le PDF au serveur local d'impression avec fetch (au lieu d'Axios)
    await fetch("http://localhost:5000/print", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        base64Data: response.data.output,
        filename: response.data.filename,
      }),
    });

    // Message de succès
    if (vm && vm.$toast) vm.$toast.success("Impression envoyée avec succès !");
  } catch (error) {
    console.error(error);
    if (vm && vm.$toast)
      vm.$toast.error("Une erreur est survenue lors de l'impression.");
  } finally {
    // Arrête le loading quoi qu’il arrive
    if (vm && vm.$loading) vm.$loading.stop();
  }
};*/
